/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { MessageBarType, Pivot, PivotItem, PrimaryButton, MessageBar, Spinner } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import SelectCobrandsForm from '../../CoBrandSettings/HubSettings/Forms/SelectCobrandsForm';
import ShowCobrandList from '../../CoBrandSettings/HubSettings/ShowCobrandList';
import AttributesTable from './Components/AttributesTable';
import Toast from '../../../Forms/Toast';
import { useFormikContext } from 'formik';

const header = css`
  border-bottom: 1px solid #ededed;
  padding-bottom: 15px;
  h4 {
    margin: 0 0 5px 0;
  }
`;

const newCoBrandSettingsObject = {
  client: '',
  cobrandTag: '',
  cobrand: '',
};

const AttributeCobrandSettingsContent = () => {
  const { values } = useFormikContext();
  const [selectedKey, setSelectedKey] = useState("0");
  const [cobrandList, setCobrandList] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [isMatchWarning, setIsMatchWarning] = useState(false);
  const [totalToUpdate, setTotalToUpdate] = useState(0);

  const [{ data, loading }, getData] = useAxios(
    { url: 'internal/cobrand/attributes', method: "get" },
    { manual: true }
  );

  const handleLinkClick = (item) => {
    const itemKey = item.props.itemKey;
    setSelectedKey(itemKey);
  };

  const getDataClick = async () => {
    if (!cobrandList || cobrandList.length === 0) {
      return;
    }

    const cobrandIdList = cobrandList.map(x => x.coBrandId).join(",");
    
    const result = await getData({
      method: "get",
      url: `internal/cobrand/attributes?cobrandIdList=${encodeURIComponent(cobrandIdList)}`,
    });

    if (!result.isAxiosError && result.data) {
      setAttributes(result.data);
      // Check if any attributes have mismatched values across cobrands
      const hasUnmatched = result.data.some(attr => !attr.isMatch);
      setIsMatchWarning(hasUnmatched);
      setSelectedKey('1');
    }
  };

  const refreshAttributes = async () => {
    if (!cobrandList || cobrandList.length === 0) {
      return;
    }
    
    const cobrandIdList = cobrandList.map(x => x.coBrandId).join(",");
    
    const result = await getData({
      method: "get",
      url: `internal/cobrand/attributes?cobrandIdList=${encodeURIComponent(cobrandIdList)}`,
    });

    if (!result.isAxiosError && result.data) {
      setAttributes(result.data);
      const hasUnmatched = result.data.some(attr => !attr.isMatch);
      setIsMatchWarning(hasUnmatched);
    }
  };

  return (
    <Form style={{ maxWidth: '1400px' }}>
      <div css={header}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }} className="mt-1">
          <h4>Attribute Cobrand Settings</h4>
        </div>
        <span>
          Select one or more cobrands to view and manage their attributes.
          {cobrandList.length > 1 && " Values marked with (*) are mixed across selected cobrands."}
        </span>
        {isMatchWarning && cobrandList.length > 1 && (
          <div style={{ marginTop: 10 }}>
            <MessageBar messageBarType={MessageBarType.warning} isMultiline={false}>
              Some attribute values do not match across the selected cobrands.
            </MessageBar>
          </div>
        )}
      </div>
      <div style={{ width: '100%' }} className="mt-1">
        <Pivot selectedKey={selectedKey} onLinkClick={handleLinkClick}>
          <PivotItem headerText="Select Cobrands" itemKey={'0'}>
            <SelectCobrandsForm />
            <ShowCobrandList
              clients={values.client}
              tags={values.cobrandTag}
              cobrands={values.cobrand}
              setTotalToUpdate={setTotalToUpdate}
              setParentCobrandList={setCobrandList}
            />
            {totalToUpdate > 0 && (
              <div style={{ marginTop: 20 }}>
                <PrimaryButton text="Next" onClick={getDataClick} />
              </div>
            )}
          </PivotItem>
          <PivotItem headerText="Attributes" itemKey={'1'} itemIcon="Database">
            {selectedKey === "1" && cobrandList.length > 0 && (
              <AttributesTable
                attributes={attributes}
                cobrandList={cobrandList}
                loading={loading}
                refreshAttributes={refreshAttributes}
                setShowToast={setShowToast}
              />
            )}
            {selectedKey === "1" && cobrandList.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Please select cobrands from the "Select Cobrands" tab first.</p>
              </div>
            )}
          </PivotItem>
        </Pivot>
      </div>
      <Toast
        message="Attribute saved successfully!"
        showToast={showToast}
        setShowToast={setShowToast}
      />
    </Form>
  );
};

const AttributeCobrandSettings = () => {
  return (
    <Formik
      initialValues={newCoBrandSettingsObject}
      isInitialValid={false}
    >
      <AttributeCobrandSettingsContent />
    </Formik>
  );
};

export default AttributeCobrandSettings;
